import ApiService from '@/services/apiService';
import { get } from 'lodash';

/**
 * Fetches device driver name from API
 * @param {number} id - Device driver ID
 * @returns {Promise<string>} Device driver name
 */
const fetchDeviceDriver = async (id) => {
  const response = await ApiService.virtualDeviceDrivers.fetchOneById(id);

  return response.title || null;
};

/**
 * Fetches device template name from API
 * @param {number} id - Device template ID
 * @returns {Promise<string>} Device template name
 */
const fetchDeviceTemplate = async (id) => {
  const response = await ApiService.deviceTemplateGetByIdProductive(id);

  return response.title || null;
};

/**
 * Fetches digital twin name from API
 * @param {number} id - Digital twin ID
 * @returns {Promise<string>} Digital twin name
 */
const fetchDigitalTwin = async (id) => {
  const response = await ApiService.digitalTwinGet(null, id, true);

  return response.title || null;
};

/**
 * Enriches search config rules by looking up device driver/template names from APIs.
 *
 * @param {Object} searchConfig - The persisted search config.
 * @returns {Promise<Object>} Enriched search config with display-ready rules.
 */
export async function prepareSearchForDisplayAsync(search) {
  const enrichedRules = await Promise.all(
    search.rules.map(async (rule) => {
      const value = rule.value?.value || rule.value;
      const enrichedRule = {
        ...rule,
        id: generateRuleId(),
      };

      if (rule.field === 'operationalStatus') {
        enrichedRule.value = parseInt(value);
      }

      if (rule.field === 'deviceDriver') {
        const name = await fetchDeviceDriver(value);
        if (name) {
          enrichedRule.text = name;
        }
      }

      if (rule.field === 'deviceTemplate') {
        const title = await fetchDeviceTemplate(value);
        if (title) {
          enrichedRule.text = title;
        }
      }

      if (rule.field === 'parentId') {
        const title = await fetchDigitalTwin(value);
        if (title) {
          enrichedRule.text = title;
        }
      }

      return enrichedRule;
    })
  );

  return {
    rules: enrichedRules,
    join: search.join || 'AND',
  };
}

/**
 * Generates a unique rule ID
 * @returns {string} Unique rule ID
 */
function generateRuleId() {
  return `rule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Checks if two rules are equal
 * @param {Object} rule1 - First rule
 * @param {Object} rule2 - Second rule
 * @returns {boolean} True if rules are equal
 */
export function areRulesEqual(rule1, rule2) {
  if (!rule1 || !rule2) {
    return false;
  }

  if (rule1.field !== rule2.field || rule1.condition !== rule2.condition) {
    return false;
  }

  if (rule1.key !== undefined || rule2.key !== undefined) {
    return rule1.key === rule2.key && rule1.value === rule2.value;
  }

  return rule1.value === rule2.value;
}

/**
 * Validates a rule
 * @param {Object} rule - Rule to validate
 * @param {Object} searchConfig - Search configuration
 * @returns {boolean} True if rule is valid
 */
export function validateRule(rule, searchConfig = {}) {
  if (!rule || !rule.field) {
    return false;
  }

  const categories = get(searchConfig, 'categories', {});
  const fieldConfig = categories[rule.field] || {};

  if (fieldConfig.type === 'keyValue') {
    const key = get(rule, 'key', '');
    const value = get(rule, 'value', '');
    return key.trim() !== '' && value.trim() !== '';
  }

  const value = get(rule, 'value', '');
  return value !== null && value !== undefined && value.toString().trim() !== '';
}
